import { useState, useEffect, useCallback } from 'react';
import { BUILD_ID } from '../config';

interface VersionInfo {
  buildId: string;
  date: string;
}

/**
 * Hook to detect application updates by polling version.json.
 * This matches the "Kaptura" pattern for reliable update detection.
 */
export function useUpdateDetection() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [lastCheck, setLastCheck] = useState<Date>(new Date());

  const checkVersion = useCallback(async () => {
    // Only check in production or if explicitly forced
    if (!import.meta.env.PROD && !localStorage.getItem('force_update_check')) return;
    
    try {
      // Append timestamp to bust cache for version.json
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) return;
      
      const remoteVersion: VersionInfo = await response.json();
      setLastCheck(new Date());

      // If remote build ID exists and is different from local, we have an update
      if (remoteVersion.buildId && remoteVersion.buildId !== BUILD_ID) {
        console.log(`[UpdateSystem] New version detected: ${remoteVersion.buildId} (Current: ${BUILD_ID})`);
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.error('[UpdateSystem] Error checking version:', error);
    }
  }, []);

  useEffect(() => {
    // Check on mount
    checkVersion();

    // Check periodically every 5 minutes
    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    // Also check when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  const applyUpdate = () => {
    // Standard reload - the browser/service worker will pick up the new assets
    // Adding a flag to clear caches if needed on boot (already handled in App.tsx diag panel)
    window.location.reload();
  };

  return { updateAvailable, applyUpdate, lastCheck };
}
